import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSetupPill } from '../onboarding/setup/pillStore';
import { agentService } from '../../_services/agent.service';
import { conciergeService } from '../../_services/concierge.service';
import axios from 'axios';
import API from '../../config/API';
import { loadPosts, subscribePosts } from '../marketing-v2/features/social/data/store';
import { loadScheduledCampaigns, todayItems } from '../marketing-v2/features/social/data/schedule';
import { AGENT_VISIBLE, CONCIERGE_VISIBLE, SOCIAL_VISIBLE } from '../../config/featureFlags';
import { can, canMarket } from '../permissions/access';
import { isHidden as isReceptionHidden, setupOff, baileyOff, approvalsOff } from '../../_utils/receptionMode';
import { subscribe, getArea, versionOf } from '../../_utils/syncSummary';
import { bookingSeriesService } from '../../_services/bookingSeries.service';
import { endsSoon, extendSeed } from '../DiaryV2/repeat/repeat';
import { openProposedBooking } from '../agent/bailey/baileyBooking';

/**
 * The bell's "To do": things that are waiting for a PERSON, as opposed to
 * Updates (things that happened). One list for the bell's badge and ring and
 * for the panel, so they can never disagree.
 *
 *   setup    - finish setting up (or a step that's held), from SetupHost
 *   post     - posts planned for now or earlier, ready to share (the old
 *              diary-header chip)
 *   ask      - Bailey waiting on an answer or an approval
 *   reply    - a client conversation the front desk flagged "needs you"
 *   message  - a client texted the location back (two-way texts) and nobody
 *              here has read it yet
 *   repeat   - a repeat booking whose last visit is within four weeks:
 *              Extend opens New Booking from the next visit, as Bailey does
 *
 * Server lists are shared across every bell on the page. They are fetched
 * when a bell first mounts (unless fetched in the last REFRESH_MS), again only
 * when the app's routine poll (/sync/summary) says that list's version moved,
 * and on demand when the panel opens. No timer of their own.
 */
const REFRESH_MS = 2 * 60 * 1000;
const MAX_EACH = 3;

let remote = { asks: [], replies: [], messages: [], repeats: [], at: 0 };
// The server doesn't list ending repeats yet: after one refusal, stop asking
// for the rest of the visit
let repeatsUnsupported = false;
let inflight = null;
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn(remote));

const pick = (res, key) => {
  const d = res && res.data;
  const body = d && (d.responseData !== undefined ? d.responseData : d.data !== undefined ? d.data : d);
  if (Array.isArray(body)) return body;
  return (body && Array.isArray(body[key]) && body[key]) || [];
};

// Which summary area stands for which list.
const PARTS = { asks: 'asks', replies: 'concierge', messages: 'clientMessages', repeats: 'bookingSeries' };
const ALL_PARTS = Object.keys(PARTS);
// The summary version each list was last fetched at (taken BEFORE the fetch,
// so a change landing mid-fetch shows as a difference next time, never hides;
// a failed fetch forgets it, so the next poll asks again).
let loadedAt = {};

function replyLink(c) {
  const clientId = c && (c.clientUserId || (c.client && (c.client._id || c.client.id)));
  return clientId ? `/clients/${encodeURIComponent(clientId)}?tab=bailey` : '/assistant/front-desk/conversations';
}

const fetchers = {
  asks: () => (AGENT_VISIBLE ? agentService.asks().then((r) => pick(r, 'asks')).catch(() => { loadedAt.asks = null; return remote.asks; }) : Promise.resolve([])),
  // The front desk's conversations are `settings` (conciergeRoute): don't ask
  // for them, or offer a link to /receptionist, without it.
  replies: () => (CONCIERGE_VISIBLE && can('settings')
    ? conciergeService.conversations({ filter: 'needs', limit: MAX_EACH + 1 }).then((r) => pick(r, 'conversations')).catch(() => { loadedAt.replies = null; return remote.replies; })
    : Promise.resolve([])),
  // Client texts nobody has read (Business numbers, two-way texts).
  messages: () => (CONCIERGE_VISIBLE
    ? axios.get(`${API.CLIENT_MESSAGES}/unread`, { headers: { accessToken: localStorage.getItem('accessToken') || '' } }).then((r) => pick(r, 'replies')).catch(() => { loadedAt.messages = null; return remote.messages; })
    : Promise.resolve([])),
  repeats: () => (repeatsUnsupported ? Promise.resolve([])
    : bookingSeriesService.endingSoon().then(l => (Array.isArray(l) ? l : (l && l.series) || []))
      .catch((e) => {
        if (e && e.response && e.response.status === 404) repeatsUnsupported = true;
        loadedAt.repeats = null;
        return remote.repeats;
      })),
};

/**
 * Fetch the To do lists. `force` skips the REFRESH_MS freshness check;
 * `parts` (default all) limits it to the lists that changed.
 */
export function refreshTodos(force, parts) {
  if (!localStorage.getItem('accessToken')) return Promise.resolve(remote);
  if (!force && Date.now() - remote.at < REFRESH_MS) return Promise.resolve(remote);
  if (inflight) return inflight.then(() => (parts ? refreshTodos(true, parts) : remote));
  const want = (parts && parts.length ? parts : ALL_PARTS).filter((p) => fetchers[p]);
  want.forEach((p) => { loadedAt[p] = versionOf(getArea(PARTS[p])); });
  inflight = Promise.all(want.map((p) => fetchers[p]())).then((lists) => {
    const next = { ...remote, at: Date.now() };
    want.forEach((p, i) => {
      const l = lists[i] || [];
      next[p] = p === 'replies' ? l.filter((x) => x && x.needsReply !== false) : l;
    });
    remote = next;
    inflight = null;
    emit();
    return remote;
  });
  return inflight;
}

/** The lists whose summary version moved since they were fetched. */
export function staleParts(data, event) {
  return ALL_PARTS.filter((p) => {
    const v = data[PARTS[p]];
    if (v === undefined || v === null) return false;   // not asked / not available to this person
    if (v.legacy) return event.changed.includes(PARTS[p]); // older server: its old cadence
    return v.version !== loadedAt[p];
  });
}

// Tests only.
export const _resetTodos = () => { remote = { asks: [], replies: [], messages: [], repeats: [], at: 0 }; inflight = null; loadedAt = {}; listeners.clear(); repeatsUnsupported = false; };

const ASK_ACTION = { approve: 'Approve', choose: 'Choose', confirm: 'Confirm', answer: 'Answer' };

/** Pure: the To do rows from what each source says. Tested. */
export function buildTodos({ pill, readyPosts = [], asks = [], replies = [], messages = [], repeats = [], allow = {}, now = new Date() }) {
  // What the person can open (permissions/access): a row never links to a
  // page that would refuse them. Unset = allowed (tests, older callers).
  if (allow.marketing === false) readyPosts = [];
  if (allow.settings === false) replies = [];
  // Reception mode: a locked front desk doesn't get rows for what its role
  // hides (the server refuses those screens anyway).
  if (setupOff()) pill = null; // setup is never on a locked device
  if (isReceptionHidden('marketing')) readyPosts = [];
  if (baileyOff()) asks = [];
  else if (approvalsOff()) asks = asks.filter((a) => a && a.kind !== 'approve');
  if (isReceptionHidden(['aiReceptionist', 'clientDetails'])) replies = [];
  if (isReceptionHidden(['clientMessages', 'clientDetails'])) messages = [];
  const setup = pill && pill.kind === 'setup' ? pill : null;
  const rows = [];
  if (pill && pill.kind !== 'setup') {
    rows.push({ id: 'held', kind: 'setup', label: 'Setup', title: pill.title, sub: pill.detail, action: pill.go || 'Open', run: pill.open });
  }
  if (readyPosts.length) {
    rows.push({
      id: 'posts', kind: 'post', label: 'Social',
      title: readyPosts.length === 1 ? 'Post ready to share' : `${readyPosts.length} posts ready to share`,
      sub: readyPosts.length === 1 ? (readyPosts[0].name || 'Planned for today') : 'Planned for today',
      action: 'Share', to: '/marketing-social',
    });
  }
  asks.slice(0, MAX_EACH).forEach((a) => rows.push({
    id: `ask:${a._id || a.id}`, kind: 'ask', label: 'Bailey asks',
    title: a.title || 'Bailey needs an answer',
    sub: a.kind === 'approve' ? 'Waiting for your OK' : 'Waiting for your answer',
    action: ASK_ACTION[a.kind] || 'Open', to: '/assistant',
  }));
  replies.slice(0, MAX_EACH).forEach((c) => rows.push({
    id: `reply:${c._id}`, kind: 'reply', label: 'Front desk',
    title: `${(c.clientName || '').trim() || 'A client'} needs a reply`,
    sub: c.preview || 'Waiting in your conversations',
    // /receptionist?c= lost the conversation on its way through two redirects:
    // the live thread is on the client's page (Bailey tab), else Front desk
    action: 'Reply', to: replyLink(c),
  }));
  // One row per client who texted back, newest first.
  const byClient = new Map();
  messages.forEach((m) => { if (m && m.clientUserId && !byClient.has(m.clientUserId)) byClient.set(m.clientUserId, m); });
  [...byClient.values()].slice(0, MAX_EACH).forEach((m) => rows.push({
    id: `msg:${m.clientUserId}`, kind: 'reply', label: 'Client message',
    title: `${(m.clientName || '').trim() || 'A client'} texted you`,
    sub: m.text || 'Waiting in their messages',
    action: 'Reply', to: `/clients/${encodeURIComponent(m.clientUserId)}?tab=messages`,
  }));
  // Repeats about to run out: one row each, Extend keeps the client's slot
  if (!isReceptionHidden('diary')) {
    endsSoon(repeats, now).slice(0, MAX_EACH).forEach((x) => {
      const name = ((x.client && x.client.name) || x.clientName || '').trim() || 'A client';
      const last = new Date(`${x.lastVisit.date}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
      const left = Number(x.upcoming) || 0;
      rows.push({
        id: `repeat:${x.seriesId}`, kind: 'repeat', label: 'Repeat booking',
        title: `${name}’s repeat ends ${last}`,
        sub: `${x.said ? `${x.said}. ` : ''}${left > 0 ? `${left} visit${left === 1 ? '' : 's'} left. ` : ''}Extend it to keep their slot.`,
        action: 'Extend',
        run: (history) => openProposedBooking(extendSeed(x), history),
      });
    });
  }
  return {
    setup,
    rows,
    // What the bell counts: setup is one thing, every row is one thing.
    count: rows.length + (setup ? 1 : 0),
  };
}

/** The bell's To do, live. `active` turns the server polling on. */
export function useTodos(active = true) {
  const dispatch = useDispatch();
  const pill = useSetupPill();
  const [srv, setSrv] = useState(remote);
  const [posts, setPosts] = useState(() => (SOCIAL_VISIBLE ? loadPosts() : []));
  const [campaigns, setCampaigns] = useState([]);
  const [, tick] = useState(0);

  useEffect(() => {
    listeners.add(setSrv);
    setSrv(remote);
    return () => { listeners.delete(setSrv); };
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    refreshTodos(false);
    // Refetch a list only when the routine poll says it changed.
    const areas = [AGENT_VISIBLE && 'asks', CONCIERGE_VISIBLE && 'concierge', CONCIERGE_VISIBLE && 'clientMessages', 'bookingSeries'].filter(Boolean);
    if (!areas.length) return undefined;
    return subscribe({
      areas,
      onUpdate: (data, event) => {
        if (event.error) return;
        const parts = staleParts(data, event);
        if (parts.length) refreshTodos(true, parts);
      },
    });
  }, [active]);

  useEffect(() => {
    if (!SOCIAL_VISIBLE) return undefined;
    let alive = true;
    const off = subscribePosts(() => setPosts(loadPosts()));
    loadScheduledCampaigns(dispatch).then((l) => { if (alive) setCampaigns(l); }).catch(() => {});
    // A post planned for 3pm becomes "ready" at 3pm.
    const t = setInterval(() => tick((n) => n + 1), 60000);
    return () => { alive = false; off(); clearInterval(t); };
  }, [dispatch]);

  const now = new Date();
  const readyPosts = SOCIAL_VISIBLE ? todayItems(posts, campaigns, now).due.filter((p) => new Date(p.plannedAt) <= now) : [];
  return buildTodos({ pill, readyPosts, asks: srv.asks, replies: srv.replies, messages: srv.messages, repeats: srv.repeats, allow: { marketing: canMarket(), settings: can('settings') } });
}
